import React from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { format, addMonths, subMonths, endOfMonth, isBefore, isAfter, startOfToday, startOfDay } from 'date-fns';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

interface CalendarPickerProps {
  selectedDate?: Date;
  onSelectDate: (date: Date | undefined) => void;
  availableDates: string[]; // Array of YYYY-MM-DD strings
  currentMonth: Date;
  onMonthChange: (month: Date) => void;
  loading?: boolean;
  className?: string;
  minDate?: Date; // Minimum date allowed for booking
  maxDate?: Date; // Maximum date allowed for booking
}

export const CalendarPicker: React.FC<CalendarPickerProps> = ({
  selectedDate,
  onSelectDate,
  availableDates,
  currentMonth,
  onMonthChange,
  loading = false,
  className,
  minDate,
  maxDate,
}) => {
  const { toast } = useToast();
  const today = startOfToday();
  const effectiveMinDate = minDate || today;
  const effectiveMaxDate = maxDate;

  // Convert availableDates strings to Date objects for comparison
  const availableDateSet = new Set(availableDates);

  const isDateAvailable = (date: Date): boolean => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return availableDateSet.has(dateStr);
  };

  const isDateDisabled = (date: Date): boolean => {
    const normalizedDate = startOfDay(date);
    const normalizedMinDate = startOfDay(effectiveMinDate);
    const normalizedMaxDate = effectiveMaxDate ? startOfDay(effectiveMaxDate) : null;

    // Disable dates before minimum date
    if (isBefore(normalizedDate, normalizedMinDate)) return true;
    // Disable dates after maximum date
    if (normalizedMaxDate && isAfter(normalizedDate, normalizedMaxDate)) return true;
    // Once availability has loaded, gray out any date that isn't available.
    // Skipped while loading so dates don't briefly flash as disabled before
    // the first fetch resolves.
    if (!loading && !isDateAvailable(date)) return true;
    return false;
  };

  const handlePrevMonth = () => {
    onMonthChange(subMonths(currentMonth, 1));
  };

  const handleNextMonth = () => {
    onMonthChange(addMonths(currentMonth, 1));
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) {
      onSelectDate(undefined);
      return;
    }

    const normalizedDate = startOfDay(date);

    if (isDateDisabled(normalizedDate)) {
      toast({
        description: 'This date is not available for booking.',
        variant: 'destructive',
        duration: 2500,
      });
      return;
    }

    if (availableDates.length > 0 && !isDateAvailable(normalizedDate)) {
      toast({
        description: 'No available slots on this date. Please choose another.',
        variant: 'destructive',
        duration: 2500,
      });
      return;
    }

    onSelectDate(normalizedDate);
  };

  return (
    <div className={cn('w-full', className)}>
      {/* Custom header with month navigation */}
      <div className="flex items-center justify-between mb-5 px-1">
        <Button
          variant="outline"
          size="icon"
          onClick={handlePrevMonth}
          disabled={isBefore(endOfMonth(subMonths(currentMonth, 1)), startOfDay(effectiveMinDate))}
          className="h-9 w-9 rounded-lg"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <h3 className="text-lg font-semibold text-slate-900">
          {format(currentMonth, 'MMMM yyyy')}
        </h3>

        <Button
          variant="outline"
          size="icon"
          onClick={handleNextMonth}
          disabled={effectiveMaxDate ? isAfter(addMonths(currentMonth, 1), effectiveMaxDate) : false}
          className="h-9 w-9 rounded-lg"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Calendar */}
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10 rounded-xl">
            <div className="animate-pulse text-teal-600 text-sm">Loading dates...</div>
          </div>
        )}

        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleDateSelect}
          month={currentMonth}
          onMonthChange={onMonthChange}
          disabled={isDateDisabled}
          modifiers={{
            available: (date) => isDateAvailable(date) && !isBefore(date, effectiveMinDate) && (!effectiveMaxDate || !isAfter(date, effectiveMaxDate)),
          }}
          modifiersClassNames={{
            available: 'bg-teal-50 text-teal-700 font-medium hover:!bg-teal-100',
          }}
          className="rounded-xl border-0 p-0 w-full"
          classNames={{
            months: 'flex flex-col w-full',
            month: 'space-y-2 w-full',
            month_caption: 'hidden',
            caption_label: 'hidden',
            nav: 'hidden',
            month_grid: 'w-full border-collapse',
            weekdays: 'flex w-full',
            weekday: 'text-slate-500 font-medium text-xs uppercase tracking-wide flex-1 text-center py-2',
            week: 'flex w-full hover:bg-transparent',
            day: 'group flex-1 text-center text-sm p-1 relative focus-within:z-20 hover:bg-transparent',
            day_button: cn(
              'w-full aspect-square max-w-12 mx-auto p-0 font-normal rounded-full text-sm',
              'hover:bg-slate-100 focus:bg-slate-100',
              'inline-flex items-center justify-center',
              // Selected state — cell parent has aria-selected="true". Using
              // group-aria-selected (instead of `[&>button]:` on the cell)
              // because Tailwind JIT picks these up reliably and the
              // specificity is unambiguous. Hover/focus must repeat the
              // selected color so they don't fall back to slate-100.
              'group-aria-selected:bg-teal-600 group-aria-selected:text-white',
              'group-aria-selected:hover:bg-teal-600 group-aria-selected:focus:bg-teal-600',
              // Today ring on the button (cell parent has data-today="true").
              'group-data-[today=true]:ring-2 group-data-[today=true]:ring-teal-600 group-data-[today=true]:ring-inset'
            ),
            selected: '',
            today: '',
            outside: 'text-slate-400',
            disabled: 'text-slate-500 cursor-not-allowed [&>button]:hover:bg-transparent',
            hidden: 'invisible',
          }}
        />
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-5 mt-5 text-xs text-slate-500">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-teal-100 border border-teal-200" />
          <span>Available</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-teal-600" />
          <span>Selected</span>
        </div>
      </div>
    </div>
  );
};

export default CalendarPicker;






